/**
 * GDSTraceabilityEditor - GDS追溯模板编辑器
 * 基于国家食品安全追溯平台风格的可视化编辑器
 */

import React, { useState } from 'react';
import { Plus, Delete, Upload, Edit, Box, FactoryBuilding, CheckOne } from '@icon-park/react';
import { uploadFiles, uploadFile, FILE_TYPES } from '../../api/upload';
import { getProxyUrl } from '../../utils/urlProxy';
import './GDSTraceabilityEditor.css';

// 图标名称到组件的映射
const iconMap = {
  box: Box,
  factory: FactoryBuilding,
  check: CheckOne,
};

// 获取图标组件
const getIconComponent = (iconName) => {
  if (!iconName) return null;
  if (React.isValidElement(iconName)) return iconName;
  if (typeof iconName === 'string') {
    const IconComponent = iconMap[iconName.toLowerCase()];
    if (IconComponent) {
      return <IconComponent theme="outline" size={16} fill="currentColor" strokeWidth={3} />;
    }
  }
  return null;
};

function GDSTraceabilityEditor({ data = {}, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 默认数据结构
  const defaultData = {
    // 基本信息
    productName: '产品名称',
    productCode: '',
    productionDate: '',
    
    // 轮播图
    carouselImages: [],
    
    // 条形码
    barcodeValue: '',
    
    // Tab内容
    tabs: [
      {
        label: '产品信息',
        icon: 'box',
        fields: [
          { label: '产品名称', value: '' },
          { label: '规格型号', value: '' },
          { label: '保质期', value: '' },
          { label: '储存条件', value: '' }
        ]
      },
      {
        label: '生产信息',
        icon: 'factory',
        fields: [
          { label: '生产企业', value: '' },
          { label: '生产地址', value: '' },
          { label: '生产日期', value: '' },
          { label: '生产批次', value: '' }
        ]
      },
      {
        label: '质检信息',
        icon: 'check',
        fields: [
          { label: '检验报告', value: '' },
          { label: '检验日期', value: '' },
          { label: '检验机构', value: '' },
          { label: '检验结果', value: '' }
        ]
      }
    ],
    
    // 认证信息
    certifications: []
  };

  const currentData = { ...defaultData, ...data };

  // ============ 轮播图上传 ============
  const handleCarouselUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadedUrls = await uploadFiles(files, {
        fileType: FILE_TYPES.IMAGE,
        folder: 'gds/carousel',
        onProgress: (percent) => setUploadProgress(percent)
      });

      const existingImages = currentData.carouselImages || [];
      onUpdate({ 
        ...currentData, 
        carouselImages: [...existingImages, ...uploadedUrls] 
      });
    } catch (error) {
      console.error('轮播图上传失败:', error);
      alert(error.message || '上传失败，请重试');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ============ 删除轮播图 ============
  const handleRemoveCarouselImage = (index) => {
    const newImages = [...(currentData.carouselImages || [])];
    newImages.splice(index, 1);
    onUpdate({ ...currentData, carouselImages: newImages });
  };

  // ============ 更新基本信息 ============
  const handleBasicInfoChange = (field, value) => {
    onUpdate({ ...currentData, [field]: value });
  };

  // ============ 更新Tab内容 ============
  const handleTabFieldChange = (tabIndex, fieldIndex, value) => {
    const newTabs = [...currentData.tabs];
    newTabs[tabIndex].fields[fieldIndex].value = value;
    onUpdate({ ...currentData, tabs: newTabs });
  };

  // ============ 添加Tab字段 ============
  const handleAddTabField = (tabIndex) => {
    const newTabs = [...currentData.tabs];
    newTabs[tabIndex].fields.push({ label: '新字段', value: '' });
    onUpdate({ ...currentData, tabs: newTabs });
  };

  // ============ 删除Tab字段 ============
  const handleRemoveTabField = (tabIndex, fieldIndex) => {
    const newTabs = [...currentData.tabs];
    newTabs[tabIndex].fields.splice(fieldIndex, 1);
    onUpdate({ ...currentData, tabs: newTabs });
  };

  // ============ 添加认证 ============
  const handleAddCertification = () => {
    const newCerts = [...(currentData.certifications || [])];
    newCerts.push({
      type: 'organic',
      label: '有机认证',
      certified: true,
      certNumber: '',
      certDate: '',
      certOrg: ''
    });
    onUpdate({ ...currentData, certifications: newCerts });
  };

  // ============ 删除认证 ============
  const handleRemoveCertification = (index) => {
    const newCerts = [...(currentData.certifications || [])];
    newCerts.splice(index, 1);
    onUpdate({ ...currentData, certifications: newCerts });
  };

  // ============ 更新认证信息 ============
  const handleCertificationChange = (index, field, value) => {
    const newCerts = [...(currentData.certifications || [])];
    newCerts[index][field] = value;
    // 当切换类型时，自动更新label
    if (field === 'type') {
      const labelMap = { gi: 'GI地理标志', organic: '有机认证', green: '绿色食品', custom: '' };
      newCerts[index].label = labelMap[value] || '';
    }
    onUpdate({ ...currentData, certifications: newCerts });
  };

  return (
    <div className="gds-template-editor">
      <h3 className="editor-section-title">GDS追溯模板配置</h3>

      {/* ============ 基本信息 ============ */}
      <section className="editor-section">
        <h4 className="section-title">基本信息</h4>
        <div className="form-grid">
          <div className="form-item">
            <label>产品名称</label>
            <input
              type="text"
              value={currentData.productName || ''}
              onChange={(e) => handleBasicInfoChange('productName', e.target.value)}
              placeholder="请输入产品名称"
            />
          </div>
          <div className="form-item">
            <label>产品编码</label>
            <input
              type="text"
              value={currentData.productCode || ''}
              onChange={(e) => handleBasicInfoChange('productCode', e.target.value)}
              placeholder="请输入产品编码"
            />
          </div>
          <div className="form-item">
            <label>生产日期</label>
            <input
              type="date"
              value={currentData.productionDate || ''}
              onChange={(e) => handleBasicInfoChange('productionDate', e.target.value)}
            />
          </div>
          <div className="form-item">
            <label>条形码值</label>
            <input
              type="text"
              value={currentData.barcodeValue || ''}
              onChange={(e) => handleBasicInfoChange('barcodeValue', e.target.value)}
              placeholder="请输入条形码数字（如：6901234567890）"
            />
          </div>
        </div>
      </section>

      {/* ============ 轮播图管理 ============ */}
      <section className="editor-section">
        <h4 className="section-title">轮播图管理</h4>
        
        <div className="carousel-manager">
          {currentData.carouselImages && currentData.carouselImages.length > 0 && (
            <div className="carousel-preview-list">
              {currentData.carouselImages.map((image, index) => (
                <div key={index} className="carousel-preview-item">
                  <img src={getProxyUrl(image)} alt={`Carousel ${index + 1}`} />
                  <button 
                    className="remove-btn"
                    onClick={() => handleRemoveCarouselImage(index)}
                  >
                    <Delete theme="filled" size={16} fill="#f44336" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <label className="upload-btn">
            <Upload theme="outline" size={18} />
            {uploading ? `上传中 ${uploadProgress}%` : '上传轮播图'}
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleCarouselUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </section>

      {/* ============ Tab内容编辑 ============ */}
      <section className="editor-section">
        <h4 className="section-title">Tab内容编辑</h4>
        
        {currentData.tabs.map((tab, tabIndex) => (
          <div key={tabIndex} className="tab-editor">
            <div className="tab-editor-header">
              <span className="tab-icon">{getIconComponent(tab.icon)}</span>
              <input
                type="text"
                className="tab-label-input"
                value={tab.label}
                onChange={(e) => {
                  const newTabs = [...currentData.tabs];
                  newTabs[tabIndex].label = e.target.value;
                  onUpdate({ ...currentData, tabs: newTabs });
                }}
              />
              <button
                className="add-field-btn"
                onClick={() => handleAddTabField(tabIndex)}
              >
                <Plus theme="outline" size={16} />
                添加字段
              </button>
            </div>

            <div className="tab-fields">
              {tab.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="field-row">
                  <input
                    type="text"
                    className="field-label"
                    value={field.label}
                    onChange={(e) => {
                      const newTabs = [...currentData.tabs];
                      newTabs[tabIndex].fields[fieldIndex].label = e.target.value;
                      onUpdate({ ...currentData, tabs: newTabs });
                    }}
                    placeholder="字段名"
                  />
                  <input
                    type="text"
                    className="field-value"
                    value={field.value}
                    onChange={(e) => handleTabFieldChange(tabIndex, fieldIndex, e.target.value)}
                    placeholder="字段值"
                  />
                  <button
                    className="remove-field-btn"
                    onClick={() => handleRemoveTabField(tabIndex, fieldIndex)}
                  >
                    <Delete theme="outline" size={16} fill="#f44336" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* ============ 认证信息管理 ============ */}
      <section className="editor-section">
        <h4 className="section-title">认证信息管理</h4>
        
        <div className="certifications-manager">
          {currentData.certifications && currentData.certifications.length > 0 && (
            <div className="certifications-list">
              {currentData.certifications.map((cert, index) => (
                <div key={index} className="cert-editor">
                  <div className="cert-header">
                    <select
                      value={cert.type}
                      onChange={(e) => handleCertificationChange(index, 'type', e.target.value)}
                    >
                      <option value="organic">有机认证</option>
                      <option value="green">绿色食品</option>
                      <option value="custom">自定义</option>
                    </select>
                    {cert.type === 'custom' && (
                      <input
                        type="text"
                        className="custom-label-input"
                        placeholder="自定义标签名称"
                        value={cert.label || ''}
                        onChange={(e) => handleCertificationChange(index, 'label', e.target.value)}
                      />
                    )}
                    <button
                      className="remove-cert-btn"
                      onClick={() => handleRemoveCertification(index)}
                    >
                      <Delete theme="filled" size={16} fill="#f44336" />
                    </button>
                  </div>

                  <div className="cert-fields">
                    <input
                      type="text"
                      placeholder="证书编号"
                      value={cert.certNumber || ''}
                      onChange={(e) => handleCertificationChange(index, 'certNumber', e.target.value)}
                    />
                    <input
                      type="date"
                      placeholder="认证日期"
                      value={cert.certDate || ''}
                      onChange={(e) => handleCertificationChange(index, 'certDate', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="认证机构"
                      value={cert.certOrg || ''}
                      onChange={(e) => handleCertificationChange(index, 'certOrg', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <button className="add-cert-btn" onClick={handleAddCertification}>
            <Plus theme="outline" size={18} />
            添加认证信息
          </button>
        </div>
      </section>

      {/* 提示信息 */}
      <div className="editor-tips">
        <p>提示：</p>
        <ul>
          <li>轮播图建议尺寸：750x500px，支持jpg/png格式</li>
          <li>条形码支持常见的13位EAN码</li>
          <li>Tab内容可自定义字段，灵活配置产品信息</li>
          <li>认证信息支持多个认证类型同时展示</li>
        </ul>
      </div>
    </div>
  );
}

export default GDSTraceabilityEditor;

